"use client";

import { useCallback, useRef } from "react";
import { Link } from "@/navigation";
import { useTranslations } from "next-intl";
import { Sparkles, ArrowRight } from "lucide-react";
import { capturePostHogEvent } from "@/lib/posthog";
import { readHomeVariant } from "@/lib/home-variant";
import { useImpression } from "@/hooks/use-impression";

/**
 * "Design your own cake" tile, sitting inline among the cake cards.
 *
 * Custom cakes are the highest-value order type and, with the calculator FAB
 * hidden, the homepage currently offers no route to one at all — `/order` lands
 * on signature selection. Placing the entry point among the cakes puts it where
 * someone forms the thought "none of these is quite it", instead of asking them
 * to find a separate section further down.
 *
 * Hidden in variant A by CSS so the control keeps today's markup exactly.
 */
export function CustomCakeTile() {
  const t = useTranslations("cakesGrid");
  const tileRef = useRef<HTMLAnchorElement>(null);

  // Impression tracking exists here to serve the cookie pretotype next door.
  //
  // Click counts alone cannot tell "nobody wants cookies" apart from "nobody
  // scrolled that far" — and grid position swamps everything else: measured
  // over the first two days of the B grid, position 0 converted at 19% while
  // positions 4-9 averaged ~1.2%. Comparing a cookie tile against the global
  // 6.9% card average would therefore measure its slot, not the idea.
  //
  // This tile is the right control: same photo-less CTA-tile format, adjacent
  // slot, same session, same visitors, and it points at custom cakes — the
  // highest-value thing we already know people buy. Cookie CTR read against
  // *this* number is a fair comparison; read against a cake card it is not.
  const buildEventProperties = useCallback(
    () => ({
      placement: "grid_tile",
      surface: "homepage",
      home_variant: readHomeVariant(),
    }),
    [],
  );

  useImpression(
    tileRef,
    useCallback(() => {
      capturePostHogEvent("custom_cake_tile_impression", buildEventProperties());
    }, [buildEventProperties]),
  );

  // Layout notes:
  // - `justify-center`, not `justify-between`. As a grid item the tile
  //   stretches to the tallest card in its row, and spreading three short lines
  //   across that height leaves a large dead gap down the middle.
  // - Solid tinted fill, not a dashed border. A dashed outline around mostly
  //   empty space is the web's convention for a drop zone or a failed load, so
  //   at full card height it read as a broken card rather than an offer.
  // - `display` is intentionally left to CSS (globals.css) rather than a `flex`
  //   utility, since that is what switches the tile on for variant B.
  return (
    <Link
      ref={tileRef}
      href={"/order?type=custom" as any}
      prefetch={false}
      onClick={() => capturePostHogEvent("custom_cake_cta_clicked", buildEventProperties())}
      className="cake-custom-tile group flex-col items-start justify-center gap-3 self-stretch rounded-2xl border border-primary/25 bg-[#ffe8ef] p-5 transition-colors duration-200 hover:border-primary hover:bg-[#ffdce6] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
    >
      <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary/10 text-primary">
        <Sparkles className="h-5 w-5" aria-hidden="true" />
      </span>
      <span className="flex flex-col gap-1">
        <span className="text-sm font-semibold text-foreground sm:text-base">
          {t("customTileTitle")}
        </span>
        <span className="text-xs text-muted-foreground sm:text-sm">
          {t("customTileText")}
        </span>
      </span>
      <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary">
        {t("customTileCta")}
        <ArrowRight
          className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5"
          aria-hidden="true"
        />
      </span>
    </Link>
  );
}
